// Who may enter the Foote platform.
//
// Owner, 2026-09-20: "let's close the open door so only assigned Foote emails
// and the footedemo@test.com get in".
//
// Until this existed, every Foote screen asked one question — is there A
// Supabase session? — so any signed-in RaisedBeef account could walk into
// foote.raisedbeef.com and use a customer's branded product. It was the oldest
// open item on the platform, and DNS went live in front of it.
//
// ⚠️ BE HONEST ABOUT WHAT THIS IS. Every Foote screen reads PUBLIC market
// aggregates through anon-executable RPCs — the same USDA and DVAuction data
// the public site serves. There is no Foote business data on that surface to
// leak, and the per-person board has always been RLS self-only. So this is a
// COMMERCIAL AND BRANDING boundary: it stops a stranger using a paid product
// wearing a customer's logo. It is not, and cannot be, a data boundary,
// because the data underneath is public by design. The server-side teeth are
// in 20260920000001: only a member may create or alter a board on a partner
// surface.
//
// ⚠️ IT FAILS CLOSED. If the membership check errors — the function missing,
// the network down — the answer is "out", not "in". A gate that opens when it
// breaks is not a gate. The one exception is deliberate and narrow: see
// PARTNER_SLUG below.

import { supabase } from "@/integrations/supabase/client";

/** The only partner today. Future customers get their own slug and seats. */
export const FOOTE_PARTNER = "foote";

export type GateState = "loading" | "in" | "out";

type RpcCall = (
  fn: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message: string } | null }>;

/**
 * Is the signed-in person allowed on this partner's platform?
 *
 * Two conditions, both required: a session, and a seat. The seat is decided
 * server-side by `public.is_partner_member`, which passes a seeded member or
 * any platform admin. Membership is keyed on EMAIL because partner accounts
 * are provisioned before the person signs up.
 */
export async function checkPartnerAccess(slug = FOOTE_PARTNER): Promise<GateState> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return "out";

  try {
    // Parenthesised cast, never a const — see supabaseRpcBinding.test.ts.
    const { data, error } = await (supabase.rpc as unknown as RpcCall)(
      "is_partner_member",
      { p_slug: slug },
    );
    // ⚠️ FAIL CLOSED. An error here means we do not know whether this person
    // has a seat, and "we do not know" must not resolve to "come in".
    if (error) return "out";
    return data === true ? "in" : "out";
  } catch {
    return "out";
  }
}
